// html-builder lists plugin: thin wrapper around @threeskye/editor-component-slate's
// lists plugin, layering in html-builder-specific customisations:
//
// - Pinned exit block: when Enter/Backspace/Shift+Tab outdents a list-item out
//   of its list, the resulting block is `{ type: 'text', ... }` — html-builder's
//   paragraph/text node type.
// - Delete-key guard: prevent the default Delete action when the editor's only
//   root child is a list and the cursor is on its last item, to avoid producing
//   an empty document the flowing renderer can't handle.
//
// All multi-level behaviour (Tab indent/outdent, depth cap, normaliser fixes,
// schema heal, paste handling, cursor preservation) lives in the editor.

import { Node as SlateNode } from 'slate';
import {
	withLists as editorWithLists,
	listsOnKeyDown as editorListsOnKeyDown,
} from '@threeskye/editor-component-slate';

const TEXT_EXIT_BLOCK = (children = [{ text: '' }]) => ({ type: 'text', children });

const isListType = (type) => type === 'bulleted-list' || type === 'numbered-list';

export const withLists = editorWithLists;

export function listsOnKeyDown(editor, event) {
	if (event.key === 'Delete') {
		const { selection } = editor;
		if (selection) {
			const path = selection.anchor.path;
			const rootNode = editor.children[path[0]];
			if (rootNode && (rootNode.type === 'bulleted-list' || rootNode.type === 'numbered-list')) {
				const listItemIndex = path[1];
				if (editor.children.length === 1 && listItemIndex === rootNode.children.length - 1) {
					event.preventDefault();
				}
			}
		}
	}
	editorListsOnKeyDown(editor, event, { exitBlock: TEXT_EXIT_BLOCK });
}

/**
 * Returns the nesting depth at the given path. For a list-item, returns the
 * number of list ancestors above it minus one (0 = top-level item). For a list
 * itself, returns the depth of items inside it (0 = outer list).
 *
 * Local copy — the editor exports this as `getListDepth` from a future version,
 * at which point swap to importing from '@threeskye/editor-component-slate'.
 */
export function getListDepth(editor, path) {
	let listsSeen = 0;
	for (let i = 0; i < path.length; i++) {
		const ancestorPath = path.slice(0, i + 1);
		try {
			const node = SlateNode.get(editor, ancestorPath);
			if (node && isListType(node.type)) {
				listsSeen++;
			}
		} catch (e) {
			break;
		}
	}
	return Math.max(0, listsSeen - 1);
}

/**
 * Resolve the inline CSS for a list at a given depth, using the consumer's
 * template config. Returns `null` when no style is configured for this depth
 * (so callers can apply browser defaults via no inline style).
 *
 * @param {number} depth         - List depth from getListDepth()
 * @param {string} listType      - 'bulleted-list' or 'numbered-list'
 * @param {object} ctx
 * @param {object} ctx.config    - Backend config carrying bulletStyles/numberStyles
 *                                 (FlowContent for FlowingPage, EditableText for inline)
 * @param {object} ctx.styles    - Template styles dict (Map<String, styleObject>)
 * @param {object} ctx.data      - DataController; provides getStyle() to convert
 *                                 the semantic style object to CSS
 */
export function resolveListStyleByDepth(depth, listType, { config, styles, data }) {
	if (!config || !styles || !data || !data.getStyle) return null;
	const styleNames = listType === 'numbered-list' ? config.numberStyles : config.bulletStyles;
	if (!Array.isArray(styleNames) || styleNames.length === 0) return null;
	const clamped = Math.min(Math.max(depth, 0), styleNames.length - 1);
	const styleName = styleNames[clamped];
	if (!styleName) return null;
	const styleObj = styles[styleName];
	if (!styleObj) return null;
	return data.getStyle(styleObj);
}
